'use client';

import { useState, useMemo } from 'react';
import Image from 'next/image';
import { Search, ChevronDown, ChevronRight, FileText, Download, ExternalLink } from 'lucide-react';

export interface COAWithBatch {
    id: string;
    name: string;
    file_url: string;
    issued_date: string;
    test_lab?: string;
    is_active?: boolean;
    metadata?: {
        verify_url?: string;
    } | null;
    batches: {
        batch_number: string;
        manufacturing_date?: string;
        products: {
            name: string;
            variant_name?: string;
            image_url?: string | null;
        };
    };
}

interface CoaListProps {
    initialCoas: COAWithBatch[];
}

export default function CoaList({ initialCoas }: CoaListProps) {
    const [coas] = useState<COAWithBatch[]>(initialCoas);
    const [searchTerm, setSearchTerm] = useState('');

    const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
    const [expandedVariants, setExpandedVariants] = useState<Set<string>>(new Set());

    const handleDownload = async (e: React.MouseEvent, url: string, baseFilename: string) => {
        e.preventDefault();

        // Extract the true extension from the file URL (e.g., .png, .jpg, .pdf)
        const extensionMatch = url.match(/\.([a-zA-Z0-9]+)(?:[\?#]|$)/);
        const extension = extensionMatch ? extensionMatch[1].toLowerCase() : 'pdf';
        const filename = `${baseFilename}.${extension}`;

        // Supabase natively forces downloads on public URLs if you append ?download
        if (url.includes('/object/public/')) {
            const hasQueryParams = url.includes('?');
            const downloadUrl = `${url}${hasQueryParams ? '&' : '?'}download=${encodeURIComponent(filename)}`;

            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            a.target = '_blank'; // Safe fallback

            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            return;
        }

        // Fallback for any non-Supabase URLs
        window.open(url, '_blank');
    };

    const toggleProduct = (productName: string) => {
        const newExpanded = new Set(expandedProducts);
        if (newExpanded.has(productName)) {
            newExpanded.delete(productName);
        } else {
            newExpanded.add(productName);
        }
        setExpandedProducts(newExpanded);
    };

    const toggleVariant = (variantKey: string) => {
        const newExpanded = new Set(expandedVariants);
        if (newExpanded.has(variantKey)) {
            newExpanded.delete(variantKey);
        } else {
            newExpanded.add(variantKey);
        }
        setExpandedVariants(newExpanded);
    };

    const filteredCOAs = useMemo(() => {
        return coas.filter(coa => {
            const term = searchTerm.toLowerCase();
            return (
                coa.name.toLowerCase().includes(term) ||
                coa.batches.batch_number.toLowerCase().includes(term) ||
                coa.batches.products.name.toLowerCase().includes(term) ||
                (coa.batches.products.variant_name || '').toLowerCase().includes(term)
            );
        });
    }, [coas, searchTerm]);

    // Grouping: Product -> Variant -> COAs
    const groupedData = useMemo(() => {
        const groups: Record<string, Record<string, COAWithBatch[]>> = {};

        filteredCOAs.forEach(coa => {
            const productName = coa.batches.products.name;
            const variantName = coa.batches.products.variant_name || 'Standard';

            if (!groups[productName]) {
                groups[productName] = {};
            }
            if (!groups[productName][variantName]) {
                groups[productName][variantName] = [];
            }
            groups[productName][variantName].push(coa);
        });

        return groups;
    }, [filteredCOAs]);

    return (
        <div className="min-h-screen bg-white pt-24 pb-20 px-4 md:px-8 font-sans selection:bg-neutral-900 selection:text-white">
            <div className="max-w-6xl mx-auto">
                {/* Header Sequence */}
                <div className="mb-16 border-b border-neutral-200 pb-12">
                    <p className="text-sm font-semibold text-blue-600 mb-2">
                        Transparent Quality
                    </p>
                    <h1 className="text-4xl md:text-5xl font-light text-neutral-900 tracking-tight mb-6">
                        Certificates of Analysis
                    </h1>
                    <p className="text-base text-neutral-600 max-w-2xl leading-relaxed">
                        View detailed batch-specific testing results for all our products. We verify purity through independent third-party laboratories to ensure {'>'}99% purity.
                    </p>
                </div>

                {/* Tactical Search */}
                <div className="mb-12 flex flex-col md:flex-row justify-between items-end gap-6 border-b border-neutral-200 pb-4">
                    <div className="relative w-full md:w-96 flex items-center">
                        <Search className="absolute left-0 text-neutral-400" size={16} />
                        <input
                            type="text"
                            placeholder="Search by product or batch..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-8 pr-4 py-2 bg-transparent text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
                        />
                    </div>
                    <div className="text-xs text-neutral-500">
                        Showing {filteredCOAs.length} of {coas.length} results
                    </div>
                </div>

                {/* Data Dictionary */}
                {Object.keys(groupedData).length === 0 ? (
                    <div className="py-24 text-center border border-neutral-200 bg-neutral-50">
                        <p className="text-sm text-neutral-500">
                            We couldn't find any certificates matching your search.
                        </p>
                    </div>
                ) : (
                    <div className="border-t border-l border-r border-neutral-200">
                        {Object.entries(groupedData).sort(([a], [b]) => a.localeCompare(b)).map(([productName, variants]) => {
                            const isProductExpanded = expandedProducts.has(productName);
                            const firstVariantCoas = Object.values(variants)[0];
                            const productImageUrl = firstVariantCoas?.[0]?.batches?.products?.image_url;
                            const totalVariants = Object.keys(variants).length;

                            return (
                                <div key={productName} className="border-b border-neutral-200 group">
                                    {/* Product Top Level Row */}
                                    <button
                                        onClick={() => toggleProduct(productName)}
                                        className="w-full grid grid-cols-[auto_1fr_auto] items-center gap-6 p-4 md:p-6 bg-white hover:bg-neutral-50 transition-colors"
                                    >
                                        <div className="w-12 h-12 relative grayscale bg-neutral-100 border border-neutral-200 flex items-center justify-center overflow-hidden shrink-0">
                                            {productImageUrl ? (
                                                <Image
                                                    src={productImageUrl}
                                                    alt={productName}
                                                    fill
                                                    sizes="48px"
                                                    className="object-cover mix-blend-multiply opacity-80"
                                                />
                                            ) : (
                                                <span className="text-[10px] font-mono text-neutral-400">IMG</span>
                                            )}
                                        </div>
                                        <div className="text-left flex flex-col items-start">
                                            <h2 className="text-lg font-medium text-neutral-900 tracking-tight">{productName}</h2>
                                            <span className="text-xs text-neutral-500 mt-1">
                                                {totalVariants} product size{totalVariants !== 1 ? 's' : ''} available
                                            </span>
                                        </div>
                                        <div className="text-neutral-300 group-hover:text-neutral-900 transition-colors">
                                            {isProductExpanded ? <ChevronDown size={18} strokeWidth={1.5} /> : <ChevronRight size={18} strokeWidth={1.5} />}
                                        </div>
                                    </button>

                                    {/* Sub-table: Variants */}
                                    {isProductExpanded && (
                                        <div className="bg-neutral-50 border-t border-neutral-200">
                                            {Object.entries(variants).sort(([a], [b]) => a.localeCompare(b)).map(([variantName, variantCoas]) => {
                                                const variantKey = `${productName}-${variantName}`;
                                                const isVariantExpanded = expandedVariants.has(variantKey);

                                                return (
                                                    <div key={variantKey} className="border-b border-neutral-200 last:border-b-0">
                                                        <button
                                                            onClick={() => toggleVariant(variantKey)}
                                                            className="w-full flex items-center justify-between p-4 pl-12 md:pl-28 hover:bg-neutral-200/30 transition-colors"
                                                        >
                                                            <div className="flex items-center gap-4">
                                                                <h3 className="text-sm font-semibold text-neutral-900">{variantName}</h3>
                                                                <span className="text-xs text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5">
                                                                    {variantCoas.length} batches
                                                                </span>
                                                            </div>
                                                            <div className="text-neutral-400">
                                                                {isVariantExpanded ? <ChevronDown size={16} strokeWidth={1.5} /> : <ChevronRight size={16} strokeWidth={1.5} />}
                                                            </div>
                                                        </button>

                                                        {/* Sub-sub-table: COAs Data Rows */}
                                                        {isVariantExpanded && (
                                                            <div className="pl-12 md:pl-28 bg-white border-t border-neutral-200">
                                                                {/* Column Headers */}
                                                                <div className="hidden md:grid grid-cols-12 gap-4 py-3 px-4 border-b border-neutral-200 text-xs font-semibold text-neutral-500 bg-neutral-50">
                                                                    <div className="col-span-3">Batch Number</div>
                                                                    <div className="col-span-2">Status</div>
                                                                    <div className="col-span-2">Date Issued</div>
                                                                    <div className="col-span-3">Laboratory</div>
                                                                    <div className="col-span-2 text-right">Document</div>
                                                                </div>

                                                                {/* Data Rows */}
                                                                <div className="divide-y divide-neutral-100">
                                                                    {variantCoas.map(coa => (
                                                                        <div key={coa.id} className="grid grid-cols-1 md:grid-cols-12 gap-y-2 gap-x-4 p-4 items-center hover:bg-neutral-50 transition-colors group/row">
                                                                            {/* Mobile Labels are shown via md:hidden, hiding desktop headers */}
                                                                            <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                                                                                <span className="md:hidden text-xs font-semibold text-neutral-500 w-24">Batch:</span>
                                                                                <span className="font-mono text-xs text-neutral-900">{coa.batches.batch_number}</span>
                                                                            </div>

                                                                            <div className="col-span-12 md:col-span-2 flex items-center gap-2">
                                                                                <span className="md:hidden text-xs font-semibold text-neutral-500 w-24">Status:</span>
                                                                                {coa.is_active !== false ? (
                                                                                    <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1.5">
                                                                                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                                                                                        Active
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="text-xs font-semibold text-neutral-500 bg-neutral-100 rounded-sm px-1.5 py-0.5">
                                                                                        Archived
                                                                                    </span>
                                                                                )}
                                                                            </div>

                                                                            <div className="col-span-12 md:col-span-2 flex items-center gap-2">
                                                                                <span className="md:hidden text-xs font-semibold text-neutral-500 w-24">Issued:</span>
                                                                                <span className="text-xs text-neutral-600">{new Date(coa.issued_date).toLocaleDateString()}</span>
                                                                            </div>

                                                                            <div className="col-span-12 md:col-span-3 flex items-center gap-2">
                                                                                <span className="md:hidden text-xs font-semibold text-neutral-500 w-24">Lab:</span>
                                                                                <span className="text-xs text-neutral-600 truncate" title={coa.test_lab}>{coa.test_lab || '--'}</span>
                                                                            </div>

                                                                            <div className="col-span-12 md:col-span-2 flex items-center justify-start md:justify-end gap-3 mt-2 md:mt-0">
                                                                                {coa.metadata?.verify_url && (
                                                                                    <a
                                                                                        href={coa.metadata.verify_url}
                                                                                        target="_blank"
                                                                                        rel="noopener noreferrer"
                                                                                        className="inline-flex items-center justify-center font-semibold rounded-lg transition-all duration-200 bg-transparent text-[#005555] border-2 border-[#005555] hover:bg-[#005555]/5 hover:shadow-md hover:shadow-[#005555]/10 text-xs px-4 py-2 gap-1.5"
                                                                                    >
                                                                                        Verify <ExternalLink size={14} />
                                                                                    </a>
                                                                                )}
                                                                                <a
                                                                                    href={coa.file_url}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
                                                                                >
                                                                                    View PDF
                                                                                </a>
                                                                                <button
                                                                                    onClick={(e) => handleDownload(e, coa.file_url, `COA-${coa.batches.batch_number}`)}
                                                                                    className="p-1.5 rounded bg-neutral-100 text-neutral-600 hover:bg-neutral-200 hover:text-blue-600 transition-colors cursor-pointer"
                                                                                    title="Download PDF"
                                                                                >
                                                                                    <Download size={14} strokeWidth={2} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Authority Footer block */}
                <div className="mt-20 border-t-2 border-neutral-900 pt-8 grid grid-cols-1 md:grid-cols-3 gap-8 text-neutral-900">
                    <div>
                        <FileText size={24} className="mb-4" strokeWidth={1} />
                        <h3 className="text-sm font-semibold mb-2">Guaranteed Quality</h3>
                        <p className="text-sm text-neutral-500 leading-relaxed">
                            Every document available here is an authentic,
                            unmodified report directly from an independent testing laboratory.
                        </p>
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold mb-2">Testing Standards</h3>
                        <div className="space-y-2 mt-4 text-sm text-neutral-600">
                            <div className="flex justify-between border-b border-neutral-100 pb-2">
                                <span>Minimum Purity Guarantee</span> <span className="font-semibold text-neutral-900">{'>'}99.0%</span>
                            </div>
                            <div className="flex justify-between border-b border-neutral-100 pb-2">
                                <span>Batches Tested</span> <span className="font-semibold text-neutral-900">100%</span>
                            </div>
                            <div className="flex justify-between pb-2">
                                <span>Testing Location</span> <span className="font-semibold text-neutral-900">United States</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
